import React, { useState, useEffect } from 'react';
import { 
  X, Check, Download, Monitor, Laptop, Shield, Sparkles, 
  Layers, Search, CheckCircle2, SlidersHorizontal, RefreshCw,
  ExternalLink, ArrowDownToLine, Zap, CheckCircle, Key, HardDrive, FolderCheck, Cpu
} from 'lucide-react';
import agentsConfig from '../agentsConfig.json';
import { getDesktopModules, toggleDesktopModule, enableAllDesktopModules, isDesktopApp, detectHardwareTokens, HardwareTokenInfo } from '../utils/desktopBridge';
import { getVaultDocuments } from '../utils/localVault';
import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, Language } from '../utils/i18n';

interface DesktopAppStoreModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLang: Language;
}

export const DesktopAppStoreModal: React.FC<DesktopAppStoreModalProps> = ({
  isOpen,
  onClose,
  currentLang
}) => {
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [downloadingPlatform, setDownloadingPlatform] = useState<string | null>(null);
  const [hardwareTokens, setHardwareTokens] = useState<HardwareTokenInfo[]>([]);
  const [vaultDocumentCount, setVaultDocumentCount] = useState<number>(0);
  const [scanningTokens, setScanningTokens] = useState(false);

  const loadData = async () => {
    setEnabledModules(getDesktopModules());
    setVaultDocumentCount(getVaultDocuments().length);
    setScanningTokens(true);
    try {
      const detected = await detectHardwareTokens();
      setHardwareTokens(detected);
    } catch (err) {
      console.warn('Error detecting tokens:', err);
    } finally {
      setScanningTokens(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const isUk = currentLang === 'uk';
  const isPl = currentLang === 'pl';
  const isDe = currentLang === 'de';
  const isFr = currentLang === 'fr';

  const handleToggle = (agentId: string) => {
    const isCurrentlyEnabled = enabledModules.includes(agentId);
    const updated = toggleDesktopModule(agentId, !isCurrentlyEnabled);
    setEnabledModules(updated);
  };

  const handleEnableAll = () => {
    const allIds = agentsConfig.map(a => a.id);
    enableAllDesktopModules(allIds);
    setEnabledModules(allIds);
  };

  const filteredAgents = agentsConfig.filter(agent => {
    const name = getLocalizedAgentName(agent, currentLang).toLowerCase();
    const desc = getLocalizedAgentDesc(agent, currentLang).toLowerCase();
    const cat = getLocalizedAgentCategory(agent, currentLang).toLowerCase();
    const q = searchQuery.toLowerCase();

    const matchesSearch = !q || name.includes(q) || desc.includes(q) || cat.includes(q) || agent.id.includes(q);
    const matchesCategory = selectedCategory === 'all' || agent.categoryUk === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const categories = Array.from(new Set(agentsConfig.map(a => a.categoryUk)));

  const handleSimulateDownload = (platform: string) => {
    setDownloadingPlatform(platform);
    setTimeout(() => {
      setDownloadingPlatform(null);
      const link = document.createElement('a');
      link.href = `/api/download/desktop-app?platform=${platform}`;
      link.download = `DeepResearchHub-Setup-${platform}.txt`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }, 600);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-slate-950/80 backdrop-blur-md animate-fade-in overflow-y-auto">
      <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col overflow-hidden">
        
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 p-6 text-white flex items-center justify-between border-b border-slate-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-400 shrink-0 shadow-inner">
              <Monitor className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 rounded-md">
                  Tauri 2.0 • Ultra-Light Desktop (~18 MB)
                </span>
                <span className="px-2.5 py-0.5 text-[10px] font-extrabold uppercase bg-indigo-500/20 text-indigo-300 border border-indigo-400/30 rounded-md">
                  {enabledModules.length} / {agentsConfig.length} {isPl ? 'aktywnych modułów' : isDe ? 'aktive Module' : isFr ? 'modules actifs' : isUk ? 'активних модулів' : 'active modules'}
                </span>
              </div>
              <h2 className="text-xl sm:text-2xl font-black uppercase tracking-tight text-white mt-1">
                {isPl ? 'Centrum Instalacji & Modułowy Pulpit Desktop' : isDe ? 'Desktop-Installationszentrum & Modulmanager' : isFr ? 'Centre d\'Installation Desktop & Modules' : isUk ? 'Десктоп-Оболонка & Менеджер Модулів на ПК' : 'Desktop Shell & Modular Agent Manager'}
              </h2>
              <p className="text-xs text-slate-300">
                {isPl ? 'Pobierz program na swój komputer i podłączaj tylko te moduły, których potrzebujesz' : isDe ? 'Laden Sie die Desktop-App herunter und aktivieren Sie gezielt benötigte KI-Module' : isFr ? 'Téléchargez l\'application et activez uniquement les modules requis' : isUk ? 'Встановіть легку програму на компʼютер та підключайте лише ті модулі, які потрібні для вашої роботи' : 'Download native desktop shell and toggle on-demand AI modules for your workstation'}
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2.5 rounded-xl bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* System Capabilities & USB Token / Vault Status Bar */}
        <div className="bg-indigo-950/40 border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-3 flex-wrap text-[11px] font-mono text-slate-700 bg-slate-50">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Hardware Token Status */}
            <div className="flex items-center gap-1.5">
              <Key className="w-3.5 h-3.5 text-amber-600" />
              <span className="font-bold text-slate-900">
                {isPl ? 'Tokeny USB KEP / eIDAS:' : isUk ? 'USB-токени КЕП/eIDAS:' : 'Hardware Tokens:'}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-800 font-bold border border-emerald-200 flex items-center gap-1">
                <CheckCircle className="w-3 h-3 text-emerald-600" />
                {hardwareTokens.length > 0 ? (isPl ? 'Gotowy do podpisu' : isUk ? 'Алмаз-1К / Кристал підключено' : 'Ready for signing') : (isPl ? 'Skanowanie...' : isUk ? 'Сканування драйверів...' : 'Scanning...')}
              </span>
            </div>

            {/* Offline Vault Documents Count */}
            <div className="flex items-center gap-1.5">
              <FolderCheck className="w-3.5 h-3.5 text-indigo-600" />
              <span className="font-bold text-slate-900">
                {isPl ? 'Lokalne archiwum:' : isUk ? 'Локальний архів:' : 'Local Vault:'}
              </span>
              <span className="font-bold text-slate-700">
                {vaultDocumentCount} {isPl ? 'dokumentów' : isUk ? 'звітів на диску' : 'documents stored'}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[10px] text-slate-400 font-bold uppercase">
              {isDesktopApp() ? '🟢 Tauri Desktop Native' : '🌐 Cloud Webview Bridge'}
            </span>
          </div>
        </div>

        {/* Download Standalone App Banner */}
        <div className="bg-white border-b border-slate-200 p-4 sm:p-5 flex items-center justify-between gap-4 flex-wrap">
          <div className="space-y-1">
            <h3 className="text-xs sm:text-sm font-extrabold text-slate-900 uppercase flex items-center gap-1.5">
              <Download className="w-4 h-4 text-indigo-600" />
              <span>{isPl ? 'Pobierz aplikację Deep Research Hub на PC / Mac' : isUk ? 'Завантажити інсталятор Deep Research Hub на ПК / Mac' : 'Download Standalone Desktop Client'}</span>
            </h3>
            <p className="text-[11px] text-slate-500">
              {isPl ? 'Bezpieczna praca z plikami lokalnymi, tokenami USB i certyfikatami eIDAS' : isUk ? 'Пряма робота з USB-токенами КЕП, важкими файлами АВК-5, CAD та локальним архівом' : 'Direct USB hardware token access, offline document archive, zero upload limits'}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleSimulateDownload('windows')}
              disabled={!!downloadingPlatform}
              className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <Laptop className="w-3.5 h-3.5" />
              <span>{downloadingPlatform === 'windows' ? 'Формування...' : 'Windows (.exe / .msi)'}</span>
            </button>
            <button
              onClick={() => handleSimulateDownload('mac')}
              disabled={!!downloadingPlatform}
              className="px-3 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <Monitor className="w-3.5 h-3.5" />
              <span>{downloadingPlatform === 'mac' ? 'Формування...' : 'macOS Apple Silicon (.dmg)'}</span>
            </button>
            <button
              onClick={() => handleSimulateDownload('linux')}
              disabled={!!downloadingPlatform}
              className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-all border border-slate-300 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <span>Linux (.deb / AppImage)</span>
            </button>
          </div>
        </div>

        {/* Toolbar & Filter */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap bg-white">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <div className="relative w-full max-w-sm">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={isPl ? 'Szukaj modułu...' : isUk ? 'Пошук модуля за назвою чи кодом...' : 'Search module by name or code...'}
                className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:bg-white transition-all text-slate-800 font-medium"
              />
            </div>

            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl text-slate-700 font-bold focus:ring-2 focus:ring-indigo-500 cursor-pointer"
            >
              <option value="all">{isPl ? 'Wszystkie kategorie' : isUk ? 'Усі галузеві центри' : 'All Hubs'}</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleEnableAll}
              type="button"
              className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-bold transition-colors flex items-center gap-1 cursor-pointer"
            >
              <Zap className="w-3.5 h-3.5 text-emerald-600" />
              <span>{isPl ? 'Włącz wszystkie (Hub Pass)' : isUk ? '⚡ Увімкнути всі модулі (Hub Pass)' : 'Enable All Modules'}</span>
            </button>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 bg-slate-50/50">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
            {filteredAgents.map((agent) => {
              const isEnabled = enabledModules.includes(agent.id);
              const name = getLocalizedAgentName(agent, currentLang);
              const desc = getLocalizedAgentDesc(agent, currentLang);
              const category = getLocalizedAgentCategory(agent, currentLang);

              return (
                <div
                  key={agent.id}
                  className={`p-4 rounded-2xl border transition-all flex flex-col justify-between ${
                    isEnabled
                      ? 'bg-white border-indigo-300/80 shadow-md ring-1 ring-indigo-500/20'
                      : 'bg-slate-100/60 border-slate-200 opacity-70 hover:opacity-100'
                  }`}
                >
                  <div className="space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase rounded bg-slate-100 text-slate-600 border border-slate-200">
                        {category}
                      </span>
                      <button
                        onClick={() => handleToggle(agent.id)}
                        type="button"
                        className={`px-2.5 py-1 rounded-lg text-[10px] font-extrabold uppercase transition-all flex items-center gap-1 cursor-pointer ${
                          isEnabled
                            ? 'bg-emerald-500 text-white shadow-xs'
                            : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
                        }`}
                      >
                        {isEnabled ? (
                          <>
                            <Check className="w-3 h-3 stroke-[3]" />
                            <span>{isPl ? 'Aktywny' : isUk ? 'Активний' : 'Enabled'}</span>
                          </>
                        ) : (
                          <span>{isPl ? '+ Podłącz' : isUk ? '+ Підключити' : '+ Install'}</span>
                        )}
                      </button>
                    </div>

                    <h4 className="text-xs font-black uppercase text-slate-900 line-clamp-2 leading-tight">
                      {name}
                    </h4>
                    <p className="text-[11px] text-slate-500 line-clamp-3 leading-relaxed">
                      {desc}
                    </p>
                  </div>

                  <div className="pt-3 mt-3 border-t border-slate-100 flex items-center justify-between text-[10px] text-slate-400 font-mono">
                    <span>ID: {agent.id}</span>
                    <span className="text-indigo-600 font-bold">Gemini 3.7 Flash</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-4 sm:p-5 bg-white border-t border-slate-200 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <Shield className="w-4 h-4 text-emerald-600" />
            <span>
              {isPl ? 'Wszystkie moduły są szyfrowane i synchronizowane z Twoim kontem' : isUk ? 'Усі підключені модулі автоматично синхронізуються з вашим акаунтом' : 'All enabled modules sync seamlessly with your enterprise cloud account'}
            </span>
          </div>

          <button
            onClick={onClose}
            type="button"
            className="px-5 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-extrabold uppercase transition-all shadow-md cursor-pointer"
          >
            {isPl ? 'Zapisz & Zastosuj' : isUk ? 'Зберегти конфігурацію столу' : 'Save & Close'}
          </button>
        </div>

      </div>
    </div>
  );
};
